import { useState } from 'react';

/* W-053 step 3a.2 (2026-10-07) -- THE ONE PASSWORD FIELD. Every password input in the app is this component (the login page and
   the three fields of PasswordChangeForm), so "show / hide password" behaves the same everywhere (Principle 0).

   - An eye button inside the field's right edge toggles ONLY this field between type="password" and type="text".
     Default HIDDEN. Each field keeps its own state, so revealing one never reveals another.
   - The button is type="button" (it never submits the form), is keyboard-reachable, and carries aria-label + title
     "Show password" / "Hide password" and aria-pressed = the state.
   - hideSignal: when this value changes the field goes back to hidden. The parent bumps it after a SUCCESSFUL submit;
     a failed submit leaves the user's choice as it is.
   - Every other prop (id, name, autoComplete, value, onChange, required, ...) goes to the <input> unchanged, so password
     managers and the label association keep working. style is the input's own style; right padding keeps the text clear
     of the icon. */

const ICON = { width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2,
    strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true, focusable: 'false' };

export function EyeIcon({ size } = {}) {
    // size: optional px (the sidebar's "View only" pill draws it at 13 / 16); the password field keeps the default 18.
    return (
        <svg {...ICON} {...(size ? { width: size, height: size } : {})}>
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
        </svg>
    );
}

function EyeOffIcon() {
    return (
        <svg {...ICON}>
            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
            <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
            <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
            <line x1="1" y1="1" x2="23" y2="23" />
        </svg>
    );
}

const BUTTON = { position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center',
    justifyContent: 'center', width: 32, height: 32, padding: 0, background: 'none', border: 'none', borderRadius: 4,
    color: '#667085', cursor: 'pointer' };

export default function PasswordInput({ style, hideSignal = 0, ...inputProps }) {
    // shown = "revealed under the CURRENT hideSignal". A new hideSignal makes it false in that same render -- no effect,
    // so a revealed password is never painted once more after the parent asks for it hidden.
    const [shownFor, setShownFor] = useState(null);
    const shown = shownFor === hideSignal;
    const toggle = () => setShownFor(shown ? null : hideSignal);
    const label = shown ? 'Hide password' : 'Show password';
    return (
        <div style={{ position: 'relative', width: '100%' }}>
            <input {...inputProps} type={shown ? 'text' : 'password'} style={{ ...style, paddingRight: 40 }} />
            <button type="button" onClick={toggle} aria-label={label} title={label} aria-pressed={shown}
                aria-controls={inputProps.id || undefined} style={BUTTON}>
                {shown ? <EyeOffIcon /> : <EyeIcon />}
            </button>
        </div>
    );
}
